import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import { Loader2, Search, FileText, Briefcase, CreditCard, ArrowRight, Download, ShieldCheck } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { getAccountMe, createCheckout, downloadMyData, getMyAcceptances, deleteMyAccount } from "@/lib/api";
import { useAcceptanceGate } from "@/components/legal/useAcceptanceGate";

function SectionCard({ icon: Icon, title, children, testId }) {
  return (
    <div className="card-cream p-6 md:p-8" data-testid={testId}>
      {/* A missing icon must not cost the whole page. Rendering an undefined
          component throws React #130, which the error boundary turns into
          "this page failed to render" - so one forgotten prop took out the
          account page entirely, sweep history and billing included. */}
      <div className="flex items-center gap-2 mb-6">
        {Icon ? <Icon size={14} className="text-[#9a3412]" /> : null}
        <span className="tick">{title}</span>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <p className="font-mono-tech text-[12px] text-[#525252]">{children}</p>;
}

export default function Account() {
  const { isSignedIn, isLoaded } = useAuth();
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [bookingAudit, setBookingAudit] = useState(false);
  const { guard, gateNode } = useAcceptanceGate();
  const [privacy, setPrivacy] = useState({
    acceptances: null, open: false, busy: false, confirm: "", result: null,
  });

  // Loaded on demand: most visits never open this panel, and it is one more
  // query against the account on every page load if it is eager.
  const openPrivacy = async () => {
    setPrivacy((p) => ({ ...p, open: !p.open }));
    if (privacy.acceptances) return;
    try {
      const rows = await getMyAcceptances();
      setPrivacy((p) => ({ ...p, acceptances: rows }));
    } catch {
      toast.error("Could not load your acceptance history.");
    }
  };

  const exportData = async () => {
    try {
      setPrivacy((p) => ({ ...p, busy: true }));
      await downloadMyData();
      toast.success("Your data is downloading.");
    } catch {
      toast.error("Could not export your data. Please try again.");
    } finally {
      setPrivacy((p) => ({ ...p, busy: false }));
    }
  };

  const deleteAccount = async () => {
    try {
      setPrivacy((p) => ({ ...p, busy: true }));
      const result = await deleteMyAccount(privacy.confirm);
      setPrivacy((p) => ({ ...p, result, confirm: "" }));
      toast.success("Your data has been deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete your account.");
    } finally {
      setPrivacy((p) => ({ ...p, busy: false }));
    }
  };

  const bookAudit = async () => {
    try {
      setBookingAudit(true);
      await guard("checkout", async () => {
        const { url } = await createCheckout("margin_audit", null, state.data?.email);
        if (!url) throw new Error("No checkout url");
        window.location.href = url;
      });
      setBookingAudit(false);
    } catch {
      toast.error("Unable to start checkout. Please try again.");
      setBookingAudit(false);
    }
  };

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setState({ status: "signed_out", data: null, error: null });
      return;
    }
    let cancelled = false;
    getAccountMe()
      .then((data) => { if (!cancelled) setState({ status: "ready", data, error: null }); })
      .catch((e) => { if (!cancelled) setState({ status: "error", data: null, error: e }); });
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn]);

  return (
    <>
      {gateNode}
      <Nav />
      <div className="pt-32 pb-24 max-w-[1000px] mx-auto px-6 md:px-10">
        <div className="tick mb-3">My account</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-[#171717] leading-[1.02] mb-10">
          Your account.
        </h1>

        {(!isLoaded || state.status === "loading") && (
          <div className="flex items-center gap-3 text-[#404040] font-mono-tech text-[13px]">
            <Loader2 size={16} className="animate-spin text-[#e88a51]" /> Loading your account…
          </div>
        )}

        {state.status === "signed_out" && (
          <div className="card-cream p-8">
            <p className="font-mono-tech text-[13px] text-[#404040]">
              Sign in from the nav to see your account.
            </p>
          </div>
        )}

        {state.status === "error" && (
          <div className="card-cream p-8">
            <p className="font-mono-tech text-[13px] text-[#9a3412]">
              Could not load your account. Try refreshing.
            </p>
          </div>
        )}

        {state.status === "ready" && state.data && (
          <div className="space-y-6">
            <SectionCard icon={Search} title="Margin risk sweep" testId="account-free-sweep">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-serif-display text-4xl text-[#171717]">
                  {state.data.free_sweeps_remaining}
                </span>
                <span className="tick">
                  of {state.data.free_sweep_limit} remaining ({state.data.free_sweeps_used} used)
                </span>
              </div>
              <p className="font-mono-tech text-[12px] text-[#525252]">
                Signed in as {state.data.email}
              </p>
            </SectionCard>

            <SectionCard icon={FileText} title="Sweep history" testId="account-sweep-history">
              {state.data.sweep_history.length === 0 ? (
                <Empty>No sweeps claimed on this account yet.</Empty>
              ) : (
                <ul className="space-y-3">
                  {state.data.sweep_history.map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-4 font-mono-tech text-[12px]" data-testid="account-sweep-row">
                      <div>
                        <div className="text-[#171717]">{s.your_domain} vs {s.competitor_domain}</div>
                        <div className="text-[#525252]">{new Date(s.created_at).toLocaleDateString()}</div>
                      </div>
                      <Link to={`/report/${s.id}`} className="btn-secondary" data-testid="account-view-report">
                        View <ArrowRight size={12} />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            <SectionCard icon={Briefcase} title="Active projects" testId="account-active-projects">
              {(state.data.pending_intakes || []).length > 0 && (
                <ul className="space-y-3 mb-4">
                  {state.data.pending_intakes.map((p) => (
                    <li key={p.session_id} className="flex items-center justify-between gap-4 font-mono-tech text-[12px] p-3 -mx-3 rounded bg-[#eeeae2]" data-testid="account-pending-intake">
                      <div>
                        <div className="text-[#171717]">{p.package_name || "Margin Audit"} — paid {new Date(p.created_at).toLocaleDateString()}</div>
                        <div className="text-[#9a3412]">Awaiting your intake — the analyst can't start without it</div>
                      </div>
                      <Link to={`/account/audit-intake/${p.session_id}`} className="btn-primary shrink-0" data-testid="account-complete-intake">
                        Complete intake <ArrowRight size={14} />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              {state.data.active_projects.length === 0 && (state.data.pending_intakes || []).length === 0 ? (
                <div>
                  <Empty>No margin audit commissioned yet.</Empty>
                  <button
                    onClick={bookAudit}
                    disabled={bookingAudit}
                    className="btn-primary mt-4 inline-flex disabled:opacity-60"
                    data-testid="account-book-audit"
                  >
                    {bookingAudit ? "Opening Stripe…" : (<>Book a Margin Audit <ArrowRight size={14} /></>)}
                  </button>
                </div>
              ) : state.data.active_projects.length > 0 && (
                <ul className="space-y-3">
                  {state.data.active_projects.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-4 font-mono-tech text-[12px]">
                      <div>
                        <div className="text-[#171717]">{p.business_name} — {p.store_domain}</div>
                        <div className="text-[#525252]">{new Date(p.created_at).toLocaleDateString()}</div>
                      </div>
                      {p.audit_report_id ? (
                        <Link to={`/account/audit-report/${p.audit_report_id}`} className="btn-secondary shrink-0" data-testid="account-view-audit">
                          View audit <ArrowRight size={12} />
                        </Link>
                      ) : p.status === "generation_failed" ? (
                        <span className="tick text-[#9a3412]">Something went wrong — contact the analyst</span>
                      ) : p.status === "pending_review" || p.status === "delivered" ? (
                        <span className="tick text-[#9a3412]">With the analyst for final review…</span>
                      ) : (
                        <span className="tick text-[#9a3412]">Building your audit…</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            <SectionCard icon={CreditCard} title="Billing history" testId="account-billing-history">
              {state.data.billing_history.length === 0 ? (
                <Empty>No payments yet.</Empty>
              ) : (
                <table className="w-full font-mono-tech text-[12px] text-[#404040]">
                  <thead>
                    <tr className="text-left border-b border-[#ddd8ce]">
                      <th className="py-2 pr-4 tick">Package</th>
                      <th className="py-2 pr-4 tick">Amount</th>
                      <th className="py-2 pr-4 tick">Status</th>
                      <th className="py-2 pr-4 tick">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.billing_history.map((tx) => (
                      <tr key={tx.session_id} className="border-b border-[#ede9e1]">
                        <td className="py-3 pr-4 text-[#171717]">{tx.package_name}</td>
                        <td className="py-3 pr-4">{tx.currency} {tx.amount}</td>
                        <td className="py-3 pr-4">{tx.payment_status}</td>
                        <td className="py-3 pr-4">{new Date(tx.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>

            <SectionCard icon={ShieldCheck} title="Your data and privacy">
              <p className="font-mono-tech text-[12px] text-[#525252] leading-relaxed mb-4">
                What we hold about you, and what you can do with it. These are the
                rights set out in our{" "}
                <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
                   className="text-[#9a3412] underline underline-offset-2">Privacy Policy</a>.
              </p>

              <div className="flex flex-wrap gap-3 mb-4">
                <button onClick={exportData} disabled={privacy.busy}
                        className="btn-secondary disabled:opacity-60" data-testid="account-export">
                  <Download size={13} /> Download my data
                </button>
                <button onClick={openPrivacy} className="btn-secondary" data-testid="account-acceptances-toggle">
                  {privacy.open ? "Hide" : "Show"} what I've accepted
                </button>
              </div>

              {privacy.open && (
                <div className="mb-6 border-t border-[#ddd8ce] pt-4" data-testid="account-acceptances">
                  {privacy.acceptances === null ? (
                    <p className="font-mono-tech text-[12px] text-[#525252]">Loading…</p>
                  ) : privacy.acceptances.length === 0 ? (
                    <p className="font-mono-tech text-[12px] text-[#525252]">
                      Nothing recorded yet.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {privacy.acceptances.map((a) => (
                        <li key={a.id} className="font-mono-tech text-[12px] text-[#404040]">
                          <span className="text-[#171717]">{a.title}</span>
                          <span className="text-[#8a857d]"> · v{a.document_version} · {String(a.accepted_at).slice(0, 10)}</span>
                          {a.superseded && (
                            <span className="text-[#9a3412]"> · a newer version has since been published</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="border-t border-[#ddd8ce] pt-4">
                <div className="tick text-[#9a3412] mb-2">Delete my data</div>
                {privacy.result ? (
                  <p className="font-mono-tech text-[12px] text-[#404040] leading-relaxed">
                    Deleted. Your briefings, audits, intakes and enquiries are gone.
                    We have kept {privacy.result.retained.payment_transactions} payment
                    record(s) and {privacy.result.retained.terms_acceptances} terms
                    acceptance(s) — Australian tax law requires the first, and the second
                    is the record of what you agreed to.
                  </p>
                ) : (
                  <>
                    <p className="font-mono-tech text-[12px] text-[#525252] leading-relaxed mb-3">
                      This deletes your briefings, audits, intakes and enquiries, and cannot
                      be undone. Payment records and your terms acceptances are kept — we are
                      required to hold the first, and the second is the record of what you
                      agreed to. Type <span className="text-[#171717]">DELETE</span> to confirm.
                    </p>
                    <div className="flex flex-wrap gap-3 items-center">
                      <input
                        className="input-vault max-w-[180px]"
                        placeholder="DELETE"
                        value={privacy.confirm}
                        onChange={(e) => setPrivacy((p) => ({ ...p, confirm: e.target.value }))}
                        data-testid="account-delete-confirm"
                      />
                      <button
                        onClick={deleteAccount}
                        disabled={privacy.busy || privacy.confirm.trim().toUpperCase() !== "DELETE"}
                        className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="account-delete"
                      >
                        Delete my data
                      </button>
                    </div>
                  </>
                )}
              </div>
            </SectionCard>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
